/**
 * Markdown → {@link DocBlock}s: the half of the Word export that decides what an answer LOOKS like
 * as a document. Pure — no `docx`, no DOM — so the mapping that actually carries the risk (nesting,
 * list numbering, tables, inline marks) is unit-testable on its own.
 *
 * It parses with the SAME dialect the thread renders (`remark-parse` + `remark-gfm`, see
 * markdown-text.tsx), so what the reader saw is what the document gets — GFM tables, strikethrough
 * and task lists included. Constructs Word has no sensible equivalent for are dropped rather than
 * approximated: raw HTML, footnotes, link/image references. An assistant answer contains none of
 * them in practice, and a half-rendered one reads as a bug.
 */
import type { ListItem, PhrasingContent, RootContent, TableRow } from "mdast";
import remarkGfm from "remark-gfm";
import remarkParse from "remark-parse";
import { unified } from "unified";
import { remarkInlineRefs, type InlineRefResolver } from "@mdotm/shadcn/assistant-ui";
import type { DocBlock, DocParagraph, DocRun, DocTableRow } from "./docModel";

/** Configured once: plugins only register micromark extensions, which the parser reads at parse time. */
const processor = unified().use(remarkParse).use(remarkGfm);

/** The marks a run inherits from the inline nodes it sits inside. */
type Marks = Omit<DocRun, "text" | "lineBreak">;

/** Where a run of block content is being emitted — the quoting/indent/list state around it. */
type Frame = {
	/** Inside a blockquote. */
	readonly quote: boolean;
	/** Nesting depth: 0 at the top level, +1 per enclosing list item. Doubles as a list's level. */
	readonly depth: number;
};

const ROOT_FRAME: Frame = { quote: false, depth: 0 };

export type MarkdownToDocBlocksOptions = {
	/**
	 * First ordered-list instance number to hand out. A document assembled from SEVERAL markdown
	 * segments (an answer interleaved with charts) must not reuse an instance across them, or Word
	 * continues the earlier list's numbering — so the caller threads `nextListInstance` through.
	 */
	readonly firstListInstance?: number;
	/**
	 * Resolves a `sphere-handle:` link target to a real address (see `@mdotm/shadcn`'s
	 * inline-refs.tsx). The thread rewrites these as it renders, and an export that did not
	 * would put a link into a Word document whose scheme nothing can follow — a worse
	 * artefact than the plain text it started as, and one nobody sees until they click it in
	 * a document that has already been sent on.
	 *
	 * Injected rather than imported so this module stays pure and testable: what a handle
	 * resolves to is a property of a live session, not of the markdown.
	 */
	readonly resolveRef?: InlineRefResolver;
};

export type MarkdownToDocBlocksResult = {
	readonly blocks: readonly DocBlock[];
	/** Pass as the next segment's `firstListInstance`. */
	readonly nextListInstance: number;
};

export function markdownToDocBlocks(
	markdown: string,
	options: MarkdownToDocBlocksOptions = {},
): MarkdownToDocBlocksResult {
	const tree = processor.parse(markdown);
	// Run as a plain tree transform rather than a registered plugin: the resolver varies per
	// call and `processor` is configured once. A handle that resolves keeps its label and
	// gains the real address; one that does not is unwrapped to its label text.
	if (options.resolveRef) remarkInlineRefs({ resolve: options.resolveRef })(tree as never);
	const blocks: DocBlock[] = [];
	const state = { nextListInstance: options.firstListInstance ?? 1 };
	appendFlow(tree.children, blocks, state, ROOT_FRAME);
	return { blocks, nextListInstance: state.nextListInstance };
}

/** Emits a sequence of block-level nodes into `blocks`. */
function appendFlow(
	nodes: readonly RootContent[],
	blocks: DocBlock[],
	state: { nextListInstance: number },
	frame: Frame,
): void {
	for (const node of nodes) {
		switch (node.type) {
			case "paragraph":
				pushParagraph(blocks, { runs: inlineRuns(node.children, {}), ...frameProps(frame) });
				break;
			case "heading":
				pushParagraph(blocks, {
					runs: inlineRuns(node.children, {}),
					// mdast guarantees 1–6; the cast just narrows `number` to the model's union.
					heading: node.depth as NonNullable<DocParagraph["heading"]>,
					...frameProps(frame),
				});
				break;
			case "blockquote":
				appendFlow(node.children, blocks, state, { ...frame, quote: true });
				break;
			case "list": {
				const ordered = node.ordered === true;
				// A fresh instance per list node: Word restarts numbering per instance, and two ordered
				// lists in one answer are two lists, not one interrupted by prose.
				const instance = ordered ? state.nextListInstance++ : 0;
				for (const item of node.children) {
					appendListItem(item, blocks, state, frame, { ordered, level: frame.depth, instance });
				}
				break;
			}
			case "code":
				pushParagraph(blocks, {
					runs: linesToRuns(node.value, { code: true }),
					preformatted: true,
					...frameProps(frame),
				});
				break;
			case "table":
				blocks.push({ type: "table", rows: tableRows(node.children) });
				break;
			case "thematicBreak":
				blocks.push({ type: "divider" });
				break;
			default:
				// Raw HTML, definitions, footnote definitions, YAML front matter: nothing to render.
				break;
		}
	}
}

/**
 * Emits one list item. Only its FIRST paragraph carries the bullet/number — anything after it
 * (a second paragraph, a code block, a nested list) is content *under* the item and is indented
 * one level instead, which is how Word models a multi-block list item too.
 */
function appendListItem(
	item: ListItem,
	blocks: DocBlock[],
	state: { nextListInstance: number },
	frame: Frame,
	list: NonNullable<DocParagraph["list"]>,
): void {
	const inner: Frame = { ...frame, depth: list.level + 1 };
	let markerUsed = false;

	for (const child of item.children) {
		if (child.type === "paragraph" && !markerUsed) {
			markerUsed = true;
			pushParagraph(blocks, {
				// GFM task list: the checkbox is content in Word's eyes, so it becomes a leading glyph.
				runs: [...checkboxRuns(item.checked), ...inlineRuns(child.children, {})],
				list,
				...(frame.quote && { quote: true }),
			});
			continue;
		}
		appendFlow([child], blocks, state, inner);
	}
}

function checkboxRuns(checked: boolean | null | undefined): readonly DocRun[] {
	if (typeof checked !== "boolean") return [];
	return [{ text: checked ? "☑ " : "☐ " }];
}

/** The paragraph properties implied by the current frame. */
function frameProps(frame: Frame): Pick<DocParagraph, "quote" | "indentLevel"> {
	return {
		...(frame.quote && { quote: true }),
		...(frame.depth > 0 && { indentLevel: frame.depth }),
	};
}

/** Skips paragraphs that would render as blank — a stray empty line must not become a spacer. */
function pushParagraph(blocks: DocBlock[], paragraph: Omit<DocParagraph, "type">): void {
	if (paragraph.runs.length === 0) return;
	if (paragraph.runs.every((run) => run.text.trim() === "" && !run.lineBreak)) return;
	blocks.push({ type: "paragraph", ...paragraph });
}

/** A multi-line string as runs joined by line breaks — how a code block stays one paragraph. */
function linesToRuns(value: string, marks: Marks): DocRun[] {
	return value.split("\n").map((line, index) => ({ text: line, ...marks, ...(index > 0 && { lineBreak: true }) }));
}

function tableRows(rows: readonly TableRow[]): DocTableRow[] {
	const columns = rows.reduce((max, row) => Math.max(max, row.children.length), 0);
	return rows.map((row, index) => {
		const cells = row.children.map((cell) => inlineRuns(cell.children, {}));
		// Word wants every row the same width; GFM lets a row be short.
		while (cells.length < columns) cells.push([]);
		return { header: index === 0, cells };
	});
}

/** Flattens inline content into runs, carrying the marks of every node it is nested in. */
function inlineRuns(nodes: readonly PhrasingContent[], marks: Marks): DocRun[] {
	const runs: DocRun[] = [];

	for (const node of nodes) {
		switch (node.type) {
			case "text":
				runs.push({ text: node.value, ...marks });
				break;
			case "strong":
				runs.push(...inlineRuns(node.children, { ...marks, bold: true }));
				break;
			case "emphasis":
				runs.push(...inlineRuns(node.children, { ...marks, italic: true }));
				break;
			case "delete":
				runs.push(...inlineRuns(node.children, { ...marks, strike: true }));
				break;
			case "inlineCode":
				runs.push({ text: node.value, ...marks, code: true });
				break;
			case "link":
				runs.push(...inlineRuns(node.children, { ...marks, link: node.url }));
				break;
			case "image":
				// Not fetched: a remote image would make the export a network operation, and the model's
				// own pictures are charts (drawn from `plot_chart`, exported as real images elsewhere).
				runs.push({ text: node.alt || node.url, ...marks, italic: true, link: node.url });
				break;
			case "break":
				runs.push({ text: "", lineBreak: true });
				break;
			default:
				// Raw HTML, footnote references, link/image references: dropped (see the module comment).
				break;
		}
	}

	return runs;
}
